import { useEffect, type ReactNode } from "react";
import styles from "./privacy.module.css";

const PAGE_TITLE = "Privacy Policy";

/**
 * The privacy policy every Company's enquiry form links to.
 *
 * Every claim here must be one the code keeps true. It said data was deleted
 * after 7 years, and nothing deletes anything; it named payments and
 * contractors, which are the RMS's and never reach intake; and it said
 * nothing about calls being recorded or answered by AI. The retention line
 * says what happens today. When a deletion schedule exists, the number goes
 * here with it, and not before.
 *
 * It says what is collected and who it goes to, not how the product is
 * built: the providers behind it are one line, unnamed. Laid out like the
 * apex page (`home.tsx`), the other page HireOS speaks for itself on.
 */
export default function PrivacyPage() {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <span className={styles.wordmark}>HireOS</span>
        </div>
      </header>

      <main className={styles.main}>
        <div className={styles.prose}>
          <h1 className={styles.title}>Privacy policy</h1>

          <Section title="Who we are">
            <p>
              HireOS takes enquiries for the rental company you're contacting.
              That company decides what happens with your enquiry, and we handle
              it for them.
            </p>
          </Section>

          <Section title="What we collect">
            {/* Each item is a <p> so it takes the theme's body text, as every
                other line on the page does; a bare <li> inherits the page's
                larger root size instead. */}
            <ul className={styles.bullets}>
              <li>
                <p>Your name, email, phone number and organisation.</p>
              </li>
              <li>
                <p>
                  Your event details, venue address, your answers and anything
                  you write in.
                </p>
              </li>
              <li>
                <p>Files you upload, like a tech rider or site plan.</p>
              </li>
              <li>
                <p>
                  If you call, your phone number, a recording of the call and a
                  transcript of it.
                </p>
              </li>
            </ul>
          </Section>

          <Section title="Calls">
            <p>
              Calls are recorded and transcribed so the company can see exactly
              what was said. If nobody picks up, an AI receptionist may answer
              and take down your enquiry.
            </p>
          </Section>

          <Section title="How we use it">
            <p>
              To get your enquiry to the rental company and draft a quote for it.
              We don't sell your information or use it for marketing.
            </p>
            <p className={styles.followingParagraph}>
              We use service providers to run HireOS, some of them overseas. They
              only use your information to provide that service.
            </p>
          </Section>

          <Section title="How long we keep it">
            <p>
              We keep your information while the rental company uses HireOS. If
              you want it deleted sooner, email us and we'll delete it.
            </p>
          </Section>

          <Section title="Your rights">
            <p>
              Under the Privacy Act 2020 you can ask to see the information we
              hold about you, and ask us to correct or delete it. Email{" "}
              <a href="mailto:hello@loony.io" className={styles.link}>
                hello@loony.io
              </a>
              . If you're not happy with how we handle it, you can complain to the{" "}
              <a href="https://www.privacy.org.nz" className={styles.link}>
                Privacy Commissioner
              </a>
              .
            </p>
          </Section>

          <p className={styles.updated}>Last updated 14 September 2026</p>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>{title}</h2>
      {children}
    </section>
  );
}
